import * as Phaser from "phaser"
import { InputManager } from "../core/input-mapper"
import { SCENE, TEX } from "../core/keys"
import { PersistedData } from "../core/persisted-data"
import { THEME } from "../core/theme"
import { playClick, playHover } from "../core/ui-sfx"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { RetroButton } from "../ui/components"
import { applyVignetteFilter, drawScanlines } from "../ui/neon-fx"

const FONT = THEME.fonts

export class TutorialScene extends Phaser.Scene {
  private inputManager!: InputManager
  private instructionTexts: Phaser.GameObjects.Text[] = []
  private nextScene: string = SCENE.MENU
  private buttonLabel = "BACK TO TITLE"

  constructor() {
    super({ key: SCENE.TUTORIAL })
  }

  init(data?: {
    nextScene?: string
    buttonText?: string
    returnTo?: string
  }): void {
    this.instructionTexts = []
    this.nextScene = data?.nextScene || data?.returnTo || SCENE.MENU
    this.buttonLabel = data?.buttonText || "BACK TO TITLE"
  }

  create(): void {
    // Mark as seen
    PersistedData.setTutorialSeen(true)

    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    this.cameras.main.fadeIn(600, 0, 0, 0)

    // 1. Initialize Input first
    this.inputManager = new InputManager(this, {
      onDown: (_action) => {
        // Global input no longer auto-finishes
      },
    })

    // 2. Background
    const bg = this.add.graphics()
    bg.fillGradientStyle(0x0a1128, 0x0a1128, 0x02040a, 0x02040a, 1)
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)

    // 3. Main Panel
    const panelW = 540
    const panelH = 360
    const panelX = cx - panelW / 2
    const panelY = cy - panelH / 2

    const panel = this.add.graphics()
    panel.fillStyle(0x1a2b5a, 0.85)
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 12)
    panel.lineStyle(2, 0x4a90e2, 0.6)
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 12)

    panel.fillStyle(0x2a407a, 1)
    panel.fillRect(panelX + 10, panelY + 10, panelW - 20, 40)
    panel.lineStyle(1, 0xffffff, 0.2)
    panel.strokeRect(panelX + 10, panelY + 10, panelW - 20, 40)

    // 4. Title
    this.add
      .text(cx, panelY + 30, "DOG TRAINING INITIATED", {
        fontFamily: FONT.sans,
        fontSize: "18px",
        fontStyle: "900 italic",
        color: "#ffffff",
        letterSpacing: 2,
      })
      .setOrigin(0.5)

    // 5. Hero Preview
    const heroX = panelX + 90
    const heroY = cy - 20
    const hero = this.add.sprite(heroX, heroY, TEX.CHAR_BELLA)
    hero.setScale(0.5)
    hero.play("hero_bella_fly")

    const heroBg = this.add.graphics()
    heroBg.lineStyle(2, 0x44ccff, 0.2)
    heroBg.strokeCircle(heroX, heroY, 60)

    // 6. Instructions List
    const flapKey = InputManager.getLabelForAction("FLAP")
    const steps = [
      `[${flapKey}] TO FLY`,
      "HOLD TO AIM",
      "RELEASE TO FIRE",
      "HIT BUMPERS FOR COMBOS",
      "12 WAVES TO WIN",
    ]

    const listX = heroX + 80
    const startY = panelY + 95
    const rowH = 44

    steps.forEach((step, i) => {
      const text = this.add
        .text(listX, startY + i * rowH, `• ${step}`, {
          fontFamily: FONT.pixel,
          fontSize: "13px",
          color: "#44ccff",
        })
        .setOrigin(0, 0.5)
        .setAlpha(0)

      this.instructionTexts.push(text)

      this.tweens.add({
        targets: text,
        alpha: 1,
        x: "+=20",
        delay: 400 + i * 150,
        duration: 400,
        ease: "Power2",
      })
    })

    // 7. Footer Button
    const startBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: cx - 90,
      y: panelY + panelH - 55,
      width: 180,
      height: 38,
      text: this.buttonLabel,
      color: 0x3a5a9a,
      isFilled: true,
      onClick: () => this.finish(),
    })
    startBtn.setAlpha(0)
    this.tweens.add({
      targets: startBtn,
      alpha: 1,
      delay: 1400,
      duration: 400,
      onComplete: () => {
        startBtn.setSelected(true)
        playHover(this)
      },
    })

    // 8. Post FX
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.05, 3).setDepth(100)
    applyVignetteFilter(this.cameras.main, { strength: 0.25, radius: 0.8 })

    this.events.once("shutdown", () => {
      this.inputManager.destroy()
    })
  }

  private finish(): void {
    playClick(this)
    this.cameras.main.fadeOut(300, 0, 0, 0)
    this.cameras.main.once("camerafadeoutcomplete", () => {
      this.scene.start(this.nextScene)
    })
  }
}
